// ============================================================================
// Anchor-string field placement (DocuSign AutoPlace model).
//
// Place a field at every occurrence of a literal string in the document —
// e.g. put a signature tab wherever "Authorized Signature:" appears. This
// matters for TCS because the recurring agreements vary in length and page
// count, so a saved layout keyed to absolute coordinates drifts the moment
// somebody adds a paragraph. An anchor does not drift.
//
// Matching rules, chosen to match DocuSign's documented behaviour:
//   • case-insensitive
//   • whole-string: the anchor must not be part of a longer word
//   • punctuation IS significant — "Signature:" ≠ "Signature"
//
// This module is deliberately pure. pdf.js text extraction happens in the
// caller; everything here is testable without a PDF or a browser.
// ============================================================================

import type { FieldType } from './envelope-lib';

/** One extracted run of text with its box, in NORMALISED page coordinates. */
export interface TextRun {
  text: string;
  /** Left edge, 0–1 across the page width. */
  x: number;
  /** Top edge, 0–1 down the page height. */
  y: number;
  /** Width of this run, 0–1. */
  width: number;
  /** Height of this run, 0–1. */
  height: number;
  page: number;
}

export interface AnchorMatch {
  page: number;
  /** Left edge of the matched text itself, normalised. */
  x: number;
  /** Top edge of the matched text itself, normalised. */
  y: number;
  /** Width of the matched text, normalised. */
  width: number;
  height: number;
}

export interface AnchorPlacementOptions {
  /** Horizontal offset from the END of the anchor text, normalised. */
  offsetX?: number;
  /** Vertical offset from the TOP of the anchor text, normalised. */
  offsetY?: number;
  width?: number;
  height?: number;
}

export interface AnchorPlacement {
  page: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
}

const WORD = /[A-Za-z0-9]/;

/**
 * Is the match at `index` a whole-string match rather than part of a longer
 * word? Punctuation on either side is fine; an adjacent letter or digit is
 * not. "Signature" must not match inside "Signatures".
 */
export function isWholeStringMatch(
  haystack: string,
  index: number,
  length: number,
): boolean {
  const before = index > 0 ? haystack[index - 1] : '';
  const after = index + length < haystack.length
    ? haystack[index + length]
    : '';
  const firstChar = haystack[index];
  const lastChar = haystack[index + length - 1];
  // Only guard the boundary when the anchor's own edge is word-ish; an anchor
  // ending in ":" can legitimately butt up against anything.
  if (WORD.test(firstChar) && before && WORD.test(before)) return false;
  if (WORD.test(lastChar) && after && WORD.test(after)) return false;
  return true;
}

/** Every occurrence of `anchor` within a single run of text. */
function matchesInRun(run: TextRun, anchor: string): AnchorMatch[] {
  const hay = run.text;
  const needle = anchor;
  if (!needle) return [];
  const lowerHay = hay.toLowerCase();
  const lowerNeedle = needle.toLowerCase();

  const out: AnchorMatch[] = [];
  let from = 0;
  for (;;) {
    const i = lowerHay.indexOf(lowerNeedle, from);
    if (i === -1) break;
    from = i + 1;
    if (!isWholeStringMatch(hay, i, needle.length)) continue;
    // Proportional character positioning within the run. Text extraction does
    // not give per-character boxes, so this is an approximation — good enough
    // to anchor a field beside a label, which is the whole use case.
    const perChar = hay.length > 0 ? run.width / hay.length : 0;
    out.push({
      page: run.page,
      x: run.x + perChar * i,
      y: run.y,
      width: perChar * needle.length,
      height: run.height,
    });
  }
  return out;
}

/** Every occurrence of `anchor` across the whole document. */
export function findAnchors(runs: TextRun[], anchor: string): AnchorMatch[] {
  const trimmed = anchor.trim();
  if (!trimmed) return [];
  return runs
    .flatMap((r) => matchesInRun(r, trimmed))
    .sort((a, b) => (a.page - b.page) || (a.y - b.y) || (a.x - b.x));
}

/** Sensible default box for each field type, as a fraction of the page. */
export const ANCHOR_DEFAULT_SIZE: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 0.24, h: 0.045 },
  initials: { w: 0.08, h: 0.035 },
  name: { w: 0.22, h: 0.03 },
  email: { w: 0.24, h: 0.03 },
  date: { w: 0.14, h: 0.03 },
  text: { w: 0.22, h: 0.03 },
  checkbox: { w: 0.03, h: 0.025 },
};

/**
 * Turn anchor matches into field placements, positioned just AFTER the anchor
 * text on the same line by default — which is where a signature line label
 * expects its field to sit. Everything is clamped to the page.
 */
export function placementsFromAnchors(
  matches: AnchorMatch[],
  fieldType: FieldType,
  opts: AnchorPlacementOptions = {},
): AnchorPlacement[] {
  const size = ANCHOR_DEFAULT_SIZE[fieldType];
  const w = opts.width ?? size.w;
  const h = opts.height ?? size.h;
  const dx = opts.offsetX ?? 0.01;
  const dy = opts.offsetY ?? 0;

  return matches.map((m) => {
    const rawX = m.x + m.width + dx;
    const rawY = m.y + dy;
    return {
      page: m.page,
      position_x: Number(Math.min(Math.max(rawX, 0), 1 - w).toFixed(4)),
      position_y: Number(Math.min(Math.max(rawY, 0), 1 - h).toFixed(4)),
      width: w,
      height: h,
    };
  });
}

/**
 * Placements that would land on top of a field that is already there.
 * Re-running an anchor should not stack duplicates, so the caller filters
 * these out. "Same place" is deliberately generous — within 2% of the page.
 */
export function isDuplicatePlacement(
  p: AnchorPlacement,
  existing: Array<{ page: number; position_x: number; position_y: number }>,
  tolerance = 0.02,
): boolean {
  return existing.some((e) =>
    e.page === p.page &&
    Math.abs(e.position_x - p.position_x) < tolerance &&
    Math.abs(e.position_y - p.position_y) < tolerance);
}
